/**
 * A plugin's own page: the mark, what it does, the state of the account behind
 * it, sample prompts you can fire straight into chat, the MCP server it maps
 * to, the skills that pair with it, and who publishes it.
 *
 * A port of Amoreg's plugin detail page. It reads the same cached connections
 * atom the catalogue does, so arriving here from a row is a memory read — no
 * second fetch, no spinner between the two pages.
 *
 * @module components/connections/PluginDetailPage
 */
import { useNavigate, useParams } from "@tanstack/react-router";
import { ArrowRightIcon, ChevronRightIcon, PlugZapIcon } from "lucide-react";
import { useCallback, useMemo } from "react";

import { useStartChatWithText } from "../../hooks/useStartChatWithText";
import { useActiveOrgId } from "../../state/orgs";
import { cn } from "../../lib/utils";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { ListSkeleton } from "../regulation/LoadingSkeletons";
import { ConnectionsLoadError } from "./ConnectionsLoadError";
import { ConnectionsPageFrame } from "./ConnectionsPageFrame";
import { ConnectorIcon } from "./ConnectorIcon";
import {
  getPluginAccountState,
  getConnectedAccountLabel,
  getPluginDeveloper,
  getPluginPrimaryCategory,
  getPluginPrompts,
  getPluginTagline,
  getRelatedSkills,
} from "./pluginContent";
import { PluginAccountButtons } from "./PluginAccountActions";
import { SkillRow } from "./SkillRow";
import { SkillDialog } from "./SkillDetailsDialog";
import { usePluginConnections } from "./usePluginConnections";

/** The prompt chips fan out rather than stacking — the source page's look. */
const CHIP_OFFSETS = ["sm:translate-x-10", "sm:-translate-x-6", "sm:translate-x-4"];

function SectionHeading({ title, count }: { readonly title: string; readonly count?: number }) {
  return (
    <h2 className="text-base font-semibold tracking-tight text-foreground">
      {title}
      {count !== undefined ? (
        <span className="ml-2 text-sm font-normal text-muted-foreground">{count}</span>
      ) : null}
    </h2>
  );
}

export function PluginDetailPage() {
  // Rendered under `/$org/connections/$pluginId`; the org-less route only
  // redirects here, so the params are read loosely rather than pinned to one.
  const { pluginId, orgSegment } = useParams({
    strict: false,
    select: (params) => {
      const loose = params as { pluginId?: string; org?: string };
      return { pluginId: loose.pluginId ?? "", orgSegment: loose.org ?? null };
    },
  });
  const navigate = useNavigate();
  const { connections, skills, capabilities, isPending, isRefreshing, actions, error, refresh } =
    usePluginConnections();
  const busy = actions.isBusy(pluginId);

  const connection = useMemo(
    () => connections.find((item) => item.id === pluginId) ?? null,
    [connections, pluginId],
  );

  const relatedSkills = useMemo(
    () => (connection ? getRelatedSkills(connection, skills) : []),
    [connection, skills],
  );

  const backToCatalogue = useCallback(() => {
    if (orgSegment) {
      void navigate({ to: "/$org/connections", params: { org: orgSegment } });
    } else {
      void navigate({ to: "/connections" });
    }
  }, [navigate, orgSegment]);

  // Writes the prompt into the org's new-chat draft, then opens it.
  const startChat = useStartChatWithText(useActiveOrgId());
  const tryPrompt = useCallback(
    (prompt: string) => {
      if (startChat) void startChat(prompt);
    },
    [startChat],
  );

  if (connection === null) {
    return (
      <ConnectionsPageFrame>
        {isPending ? (
          <ListSkeleton label="Loading plugin" />
        ) : error ? (
          <ConnectionsLoadError hasSnapshot={false} isRefreshing={isRefreshing} onRetry={refresh} />
        ) : (
          <div className="flex min-h-[60vh] w-full flex-col items-center justify-center gap-3 text-center">
            <PlugZapIcon className="size-6 text-muted-foreground/60" />
            <p className="text-sm font-medium text-foreground">
              This plugin isn't in the catalogue
            </p>
            <p className="max-w-sm text-xs text-muted-foreground">
              It may have been disabled for your organisation, or the link is out of date.
            </p>
            <Button size="sm" variant="secondary" onClick={backToCatalogue}>
              Back to plugins
            </Button>
          </div>
        )}
      </ConnectionsPageFrame>
    );
  }

  const account = getPluginAccountState(connection, capabilities);
  const accountLabel = getConnectedAccountLabel(connection);
  const prompts = getPluginPrompts(connection);

  return (
    <ConnectionsPageFrame>
      <nav aria-label="Breadcrumb" className="flex items-center gap-1.5 text-sm">
        <Button
          variant="link"
          className="h-auto border-0 p-0 text-muted-foreground hover:text-foreground sm:h-auto"
          onClick={backToCatalogue}
        >
          Plugins
        </Button>
        <ChevronRightIcon className="size-3.5 text-muted-foreground/60" />
        <span className="font-medium text-foreground">{connection.name}</span>
      </nav>
      {error ? (
        <div className="mt-4">
          <ConnectionsLoadError hasSnapshot isRefreshing={isRefreshing} onRetry={refresh} />
        </div>
      ) : null}

      <div className="mt-10 flex size-20 items-center justify-center overflow-hidden rounded-3xl border border-border/60 bg-background p-4">
        <ConnectorIcon connection={connection} />
      </div>

      <div className="mt-6 flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0">
          <h1 className="text-3xl font-semibold tracking-tight text-foreground">
            {connection.name}
          </h1>
          <p className="mt-1.5 text-muted-foreground">{getPluginTagline(connection)}</p>
        </div>
      </div>

      <section
        className="mt-6 rounded-2xl border border-border/60 p-4 sm:p-5"
        aria-label="Account connection"
      >
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="min-w-0" aria-live="polite">
            <div className="flex items-center gap-2">
              <h2 className="text-sm font-semibold">Your account</h2>
              <Badge
                variant="outline"
                className={account.ready ? "border-success/40 text-success-foreground" : undefined}
              >
                {account.label}
              </Badge>
            </div>
            {account.connected && accountLabel ? (
              <p className="mt-2 break-all text-sm">{accountLabel}</p>
            ) : null}
            <p className="mt-2 max-w-lg text-sm text-muted-foreground">{account.description}</p>
          </div>
          <PluginAccountButtons
            connection={connection}
            capabilities={capabilities}
            actions={actions}
          />
        </div>
      </section>

      <div className="mt-8 rounded-3xl bg-gradient-to-br from-accent/60 via-muted/40 to-accent/40 px-6 py-12">
        <div className="mx-auto flex max-w-2xl flex-col items-center gap-4">
          {prompts.map((prompt, index) => (
            <Button
              key={prompt}
              variant="outline"
              className={cn(
                "h-auto max-w-full min-w-0 gap-2 rounded-full border-0 bg-background/95 py-2 pl-3 pr-2 shadow-sm backdrop-blur transition-transform enabled:hover:scale-[1.02] disabled:opacity-60 motion-reduce:transform-none sm:h-auto",
                CHIP_OFFSETS[index % CHIP_OFFSETS.length],
              )}
              disabled={!account.ready || busy}
              title={!account.ready ? account.description : undefined}
              onClick={() => tryPrompt(prompt)}
            >
              <span className="flex size-6 shrink-0 items-center justify-center overflow-hidden rounded-md">
                <ConnectorIcon connection={connection} />
              </span>
              <span className="shrink-0 text-sm font-semibold text-foreground">
                {connection.name}
              </span>
              <span className="truncate text-sm text-foreground">{prompt}</span>
              <span className="ml-1 flex size-8 shrink-0 items-center justify-center rounded-full bg-muted transition-colors hover:bg-muted/70">
                <ArrowRightIcon className="size-4" />
              </span>
            </Button>
          ))}
        </div>
      </div>

      <p className="mt-8 max-w-3xl text-[15px] leading-7 text-foreground/90">
        {connection.description}
      </p>

      {relatedSkills.length > 0 ? (
        <section className="mt-12">
          <SectionHeading count={relatedSkills.length} title="Skills" />
          <SkillDialog onSaved={refresh}>
            {(openSkill) => (
              <div className="mt-4 space-y-1">
                {relatedSkills.map((skill) => (
                  <SkillRow key={skill.id} skill={skill} onOpen={openSkill} />
                ))}
              </div>
            )}
          </SkillDialog>
        </section>
      ) : null}

      <section className="mt-12 pb-10">
        <SectionHeading title="Information" />
        <dl className="mt-4 space-y-3 text-sm">
          <div className="grid grid-cols-[140px_1fr] gap-4">
            <dt className="text-muted-foreground">Developer</dt>
            <dd className="text-foreground">{getPluginDeveloper(connection)}</dd>
          </div>
          <div className="grid grid-cols-[140px_1fr] gap-4">
            <dt className="text-muted-foreground">Category</dt>
            <dd className="text-foreground">{getPluginPrimaryCategory(connection)}</dd>
          </div>
          <div className="grid grid-cols-[140px_1fr] gap-4">
            <dt className="text-muted-foreground">Capabilities</dt>
            <dd className="text-foreground">
              {connection.capabilities.length > 0 ? connection.capabilities.join(", ") : "—"}
            </dd>
          </div>
        </dl>
      </section>
    </ConnectionsPageFrame>
  );
}
